import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CardDeck } from '@/components/CardDeck';
import { DrawnCard } from '@/components/DrawnCard';
import { CardHistory } from '@/components/CardHistory';
import { ShuffleAnimation } from '@/components/ShuffleAnimation';
import { AllCardsView } from '@/components/AllCardsView';
import { ThreeCardSpread } from '@/components/ThreeCardSpread';
import { ReadingForm } from '@/components/ReadingForm';
import { ReadingResult } from '@/components/ReadingResult';
import { SharedReading } from '@/components/SharedReading';
import { QuickReadingForm } from '@/components/QuickReadingForm';
import { ConsultingPage } from '@/components/ConsultingPage';
import { AboutPage } from '@/components/AboutPage';
import { ContactPage } from '@/components/ContactPage';
import { BlogPage } from '@/components/BlogPage';
import { BlogPostPage } from '@/components/BlogPostPage';
import { BlogAdminPage } from '@/components/BlogAdminPage';
import { AnalyticsAdminPage } from '@/components/AnalyticsAdminPage';
import { initGA, trackPageView } from '@/lib/analytics';
import { getStreak } from '@/lib/streak';
import { SiteFooter } from '@/components/SiteFooter';
import { SiteHeader } from '@/components/SiteHeader';
import { Reflections } from '@/components/Reflections';
import { CoverImage } from '@/components/CoverImage';
import { useSound } from '@/hooks/useSound';
import { shuffleDeck, MARKETING_TAROT_DECK } from '@/data/marketingTarot';
import { YouTubePlayer } from '@/components/YouTubePlayer';
import { 
  Shuffle, 
  Sparkles,
  Target,
  TrendingUp,
  Layers,
  Wand2,
  Flame,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Background stars component - pre-generated positions for consistent rendering
const starPositions = Array.from({ length: 40 }, (_, i) => ({
  id: i,
  left: `${(i * 17 + 13) % 100}%`,
  top: `${(i * 23 + 7) % 100}%`,
  animationDelay: `${(i * 0.13) % 3}s`,
  animationDuration: `${2 + (i * 0.07) % 2}s`
}));

const StarField = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    {starPositions.map((star) => (
      <div
        key={star.id}
        className="absolute w-1 h-1 bg-[#e89b47]/20 rounded-full animate-pulse"
        style={{
          left: star.left,
          top: star.top,
          animationDelay: star.animationDelay,
          animationDuration: star.animationDuration
        }}
      />
    ))}
  </div>
);

const StreakBadge = () => {
  const [streak] = useState(() => getStreak());
  if (!streak.count) return null;
  return (
    <div className="flex justify-center mt-6">
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#e89b47]/30 bg-[#e89b47]/10 animate-pulse"
        style={{ animationDuration: '3s' }}
        data-testid="streak-badge"
      >
        <Flame className="w-4 h-4 text-[#e89b47]" />
        <span className="font-body text-xs text-[#e8dcc8]">
          {streak.status === 'active'
            ? `${streak.count}-month reading streak`
            : `Keep your ${streak.count}-month streak — draw this month's reading`}
        </span>
      </div>
    </div>
  );
};

const TarotApp = () => {
  const [drawnCard, setDrawnCard] = useState(null);
  const [sessionDrawnIds, setSessionDrawnIds] = useState([]);
  const [threeCardSpread, setThreeCardSpread] = useState(null);
  const [history, setHistory] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [showAllCards, setShowAllCards] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [deck, setDeck] = useState(MARKETING_TAROT_DECK);
  const { playSound, isMuted } = useSound();
  const navigate = useNavigate();
  
  // Music state
  const [youtubeUrl, setYoutubeUrl] = useState('');
  
  // Reading feature state
  const [showReadingForm, setShowReadingForm] = useState(false);
  const [showReadingResult, setShowReadingResult] = useState(false);
  const [readingData, setReadingData] = useState(null);
  
  // Shared reading state
  const [sharedReadingId, setSharedReadingId] = useState(null);
  
  // Quick reading form state
  const [showQuickForm, setShowQuickForm] = useState(false);
  const [quickFormData, setQuickFormData] = useState(null);

  // Latest blog posts (for TMT Blog section)
  const [latestBlogPosts, setLatestBlogPosts] = useState([]);

  // Check for shared reading in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const readingId = params.get('reading');
    if (readingId) {
      setSharedReadingId(readingId);
    }
  }, []);

  // Fetch latest 2 blog posts
  useEffect(() => {
    const fetchLatest = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/posts?limit=2`);
        if (!res.ok) return;
        const data = await res.json();
        setLatestBlogPosts((data.posts || []).slice(0, 2));
      } catch {
        // silently fail — section will simply hide
      }
    };
    fetchLatest();
  }, []);

  const handleReadingSubmit = (data) => {
    setReadingData(data);
    setShowReadingForm(false);
    setShowReadingResult(true);

    // Send notification in background
    fetch(`${API_URL}/api/reading/notify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        website_url: data.websiteUrl || '',
        question: data.goal || '',
        email: data.email || '',
      }),
    }).catch(() => {});
  };

  const handleQuickFormSubmit = (data) => {
    setQuickFormData(data);
    setShowQuickForm(false);
    // Open card selection in single-select mode with shuffle
    setSelectionMode(true);
    setShowAllCards(true);
  };

  const handleReadingClose = () => {
    setShowReadingResult(false);
    setReadingData(null);
  };

  // Restore saved YouTube URL
  useEffect(() => {
    const savedUrl = sessionStorage.getItem('youtubeUrl');
    if (savedUrl) setYoutubeUrl(savedUrl);

    // Open deck view if linked from footer
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'deck') {
      setSelectionMode(false);
      setQuickFormData(null);
      setShowAllCards(true);
    }
  }, []);

  const handleMusicSubmit = (url) => {
    setYoutubeUrl(url);
    if (url) sessionStorage.setItem('youtubeUrl', url);
  };

  const handleClosePlayer = () => {
    setYoutubeUrl('');
    sessionStorage.removeItem('youtubeUrl');
  };

  // Load history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('marketingTarotHistory');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        setHistory(() => parsed);
      } catch (e) {
        console.log('Failed to load history');
      }
    }
  }, []);

  // Save history to localStorage
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('marketingTarotHistory', JSON.stringify(history));
    }
  }, [history]);

  const handleCardSelect = useCallback((card) => {
    if (sessionDrawnIds.length >= 3) {
      toast.info('Three cards per session', {
        description: 'You have drawn your three cards. Refresh the page to begin a new session.',
        duration: 4000,
      });
      return;
    }
    if (sessionDrawnIds.includes(card.id)) {
      toast.info(`${card.name} already drawn`, {
        description: 'The deck never repeats itself — choose a different card.',
        duration: 4000,
      });
      return;
    }
    setSessionDrawnIds((prev) => [...prev, card.id]);
    playSound('cardFlip');
    setDrawnCard(card);
    setThreeCardSpread(null);
    
    // Add to history
    const historyItem = {
      card,
      timestamp: Date.now()
    };
    setHistory(prev => [historyItem, ...prev.slice(0, 49)]);

    setTimeout(() => {
      playSound('reveal');
      toast.success(`${card.name} revealed!`, {
        description: card.isReversed ? 'Reversed position' : 'Upright position',
        duration: 3000,
      });
    }, 800);
  }, [playSound, sessionDrawnIds]);

  const handleDrawThreeCards = useCallback(() => {
    // Open the card selection view
    setSelectionMode(true);
    setShowAllCards(true);
  }, []);

  const handleSelectionComplete = useCallback((selectedCards) => {
    playSound('shuffle');
    setShowAllCards(false);
    setSelectionMode(false);

    // If quick form data exists (single card mode), show the drawn card + trigger reading
    if (quickFormData) {
      const card = selectedCards[0];
      setDrawnCard(card);
      setThreeCardSpread(null);

      // Add to history
      const historyItem = { card, timestamp: Date.now() };
      setHistory(prev => [historyItem, ...prev.slice(0, 49)]);

      toast.success(`${card.name} revealed!`, {
        description: card.isReversed ? 'Reversed position' : 'Upright position',
        duration: 3000,
      });

      // Trigger AI reading with the quick form context
      setReadingData({
        goal: quickFormData.question,
        audience: '',
        industry: '',
        companySize: '',
        segment: '',
        challenge: quickFormData.question,
        websiteUrl: quickFormData.websiteUrl,
        selectedCards,
      });
      setQuickFormData(null);
      setShowReadingResult(true);
      return;
    }

    // Normal 3-card flow
    setDrawnCard(null);
    setThreeCardSpread(selectedCards);
    
    selectedCards.forEach((card, index) => {
      const historyItem = {
        card,
        timestamp: Date.now() + index,
        spreadPosition: ['Past', 'Present', 'Future'][index]
      };
      setHistory(prev => [historyItem, ...prev.slice(0, 49)]);
    });

    toast.success('Your cards revealed!', {
      description: 'Click on any card to explore its meaning',
      duration: 3000,
    });
  }, [playSound, quickFormData]);

  const handleShuffle = useCallback(() => {
    playSound('shuffle');
    setIsShuffling(true);
    setDrawnCard(null);
    setThreeCardSpread(null);
  }, [playSound]);

  const handleShuffleComplete = useCallback(() => {
    setIsShuffling(false);
    setDeck(shuffleDeck(MARKETING_TAROT_DECK));
    toast.success('Deck shuffled!', {
      description: 'The cards have been mystically rearranged',
    });
  }, []);

  const handleReset = useCallback(() => {
    setDrawnCard(null);
    setThreeCardSpread(null);
  }, []);

  const handlePickAnother = useCallback(() => {
    const remaining = deck.filter((c) => !sessionDrawnIds.includes(c.id) && c.id !== drawnCard?.id);
    if (remaining.length === 0) return;
    const next = remaining[Math.floor(Math.random() * remaining.length)];
    handleCardSelect(next);
  }, [deck, drawnCard, sessionDrawnIds, handleCardSelect]);

  const handleClearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem('marketingTarotHistory');
    toast.info('History cleared');
  }, []);

  const handleSelectFromHistory = useCallback((card) => {
    setDrawnCard(card);
    setThreeCardSpread(null);
  }, []);

  const isShowingCards = drawnCard || threeCardSpread;

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628]">
      {/* Background Elements */}
      <StarField />
      
      {/* Gradient overlay */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#1a3d4a]/30 via-transparent to-[#1e2a4a]/40 pointer-events-none"/>

      {/* Shuffle Animation Overlay */}
      <ShuffleAnimation 
        isActive={isShuffling} 
        onComplete={handleShuffleComplete}
      />

      {/* All Cards View Modal */}
      <AllCardsView 
        isOpen={showAllCards}
        onClose={() => {
          setShowAllCards(false);
          setSelectionMode(false);
          setQuickFormData(null);
        }}
        selectionMode={selectionMode}
        singleSelect={!!quickFormData}
        onSelectionComplete={handleSelectionComplete}
      />

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Top Nav */}
        <SiteHeader />
        {/* Header */}
        <header className="text-center mb-12 pt-4">
          <p className="font-body text-xs uppercase tracking-[0.3em] text-[#e89b47]/60 mb-6">
            Marketing Tarot
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#e8dcc8] tracking-wider leading-tight mb-4">
            Marketing Clarity Starts Here
          </h1>
          <p className="font-body text-base sm:text-lg text-[#d4c4a8]/60 max-w-md mx-auto mb-12">
            Guided Exploration. Drawn Insights. Strategic Blueprint.
          </p>

          {/* 4-Step Alchemy Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto mb-12">
            {[
              { n: 1, title: 'Marketing Tarot Reading', sub: 'A strategic diagnostic experience' },
              { n: 2, title: 'Brand Alchemy', sub: 'Turning ideas into powerful brands' },
              { n: 3, title: 'Storytelling Alchemy', sub: 'Making brands memorable' },
              { n: 4, title: 'Community & Influence Alchemy', sub: 'Partnership strategy that builds trust and reach' },
            ].map((s) => (
              <div key={s.n} className="text-center" data-testid={`hero-step-${s.n}`}>
                <div className="w-10 h-10 rounded-full border border-[#e89b47]/30 bg-[#e89b47]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="font-display text-sm text-[#e89b47]">{s.n}</span>
                </div>
                <h3 className="font-display text-sm text-[#e8dcc8] mb-1.5">{s.title}</h3>
                <p className="font-body text-xs italic text-[#d4c4a8]/60 leading-relaxed">
                  {s.sub}
                </p>
              </div>
            ))}
          </div>

          {/* Primary CTA */}
          <Button
            onClick={() => setShowReadingForm(true)}
            className="h-14 px-10 text-base bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display tracking-widest shadow-lg shadow-[#e89b47]/25 rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
            data-testid="start-reading-btn"
          >
            Get Clarity Now
          </Button>

          {/* Sub-CTA */}
          <div className="mt-5">
            <button
              onClick={() => {
                setDrawnCard(null);
                setThreeCardSpread(null);
                const deckEl = document.querySelector('[data-testid="card-deck"]');
                if (deckEl) deckEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="font-body text-sm text-[#e89b47]/70 hover:text-[#e89b47] transition-colors inline-flex items-center gap-1.5 group"
              data-testid="explore-deck-btn"
            >
              Explore the deck
              <span className="inline-block transition-transform group-hover:translate-y-1">&darr;</span>
            </button>
          </div>

          {/* Utility icons */}
          <div className="flex items-center justify-center gap-2 mt-8">
            <Button
              onClick={handleDrawThreeCards}
              variant="ghost"
              size="sm"
              className="text-[#d4c4a8]/50 hover:text-[#e89b47] hover:bg-[#e89b47]/10 text-xs font-display tracking-wide"
              data-testid="get-reading-btn"
            >
              <Wand2 className="w-4 h-4 mr-1.5"/>
              Pick Your Best Hand
            </Button>
          </div>

          {/* Reading streak */}
          <StreakBadge />
        </header>

        {/* Card Deck - Above the panels */}
        {!drawnCard && !threeCardSpread && (
          <div className="flex flex-col items-center mb-8">
            <Button
              onClick={handleShuffle}
              disabled={isShuffling}
              className="mb-4 bg-transparent border-2 border-[#d4c4a8]/40 text-[#d4c4a8] hover:bg-[#d4c4a8]/10 hover:border-[#d4c4a8] font-display tracking-wide group"
              data-testid="shuffle-btn"
            >
              <Shuffle className="w-4 h-4 mr-2 group-hover:animate-spin"/>
              Shuffle
            </Button>
            <CardDeck
              onCardSelect={handleCardSelect}
              isShuffling={isShuffling}
            />
          </div>
        )}

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Card History - Left Side */}
          {history.length > 0 && !threeCardSpread && (
          <div className="order-3 lg:order-1">
            <CardHistory
              history={history}
              onClearHistory={handleClearHistory}
              onSelectCard={handleSelectFromHistory}
            />
          </div>
          )}

          {/* Main Card Area - Center */}
          <div className={cn("order-1", history.length > 0 ? "lg:order-2 lg:col-span-2" : "lg:col-span-3")}>
            {threeCardSpread ? (
              <ThreeCardSpread
                cards={threeCardSpread}
                onReset={handleReset}
              />
            ) : drawnCard ? (
              <DrawnCard
                card={drawnCard}
                onReset={handleReset}
                onPickAnother={handlePickAnother}
                sessionLocked={sessionDrawnIds.length >= 3}
                drawsLeft={Math.max(0, 3 - sessionDrawnIds.length)}
                userName={null}
              />
            ) : (
              <div className="flex flex-col items-center">
                {/* Instructions */}
                <div className={cn(
                  "text-center p-6 rounded-xl",
                  "bg-[#1e2a4a]/60 backdrop-blur-sm border-2 border-[#d4c4a8]/20"
                )}>
                  <Sparkles className="w-8 h-8 text-[#e89b47] mx-auto mb-3 animate-pulse"/>
                  <h2 className="font-display text-xl text-[#e8dcc8] mb-2">
                    Select Your Card
                  </h2>
                  <p className="font-body text-[#d4c4a8]/60 text-sm mb-4">
                    Click a card from the deck above, or use the buttons
                  </p>
                  
                  {/* Reading Options */}
                  <div className="border-t border-[#d4c4a8]/10 pt-4 mt-2">
                    <ul className="font-body text-[#d4c4a8]/50 text-xs space-y-2 text-left max-w-xs mx-auto">
                      <li className="flex items-start gap-2">
                        <span className="text-[#e89b47]">✦</span>
                        <span><strong className="text-[#e8dcc8]">Single Card:</strong> Quick insight for focused questions</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#e89b47]">✦</span>
                        <span><strong className="text-[#e8dcc8]">Reveal the Cards:</strong> Explore the deck and select your best 3 moves</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Our Story Section */}
        <section className="mt-20 max-w-4xl mx-auto px-2" data-testid="our-story-section">
          <div className="text-center mb-8">
            <p className="font-display text-xs tracking-[0.3em] text-[#e89b47] mb-4 uppercase">
              Our Story
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#e8dcc8] leading-tight mb-6">
              Marketing is a reading of <span className="text-[#e89b47]">human behaviour</span>.
            </h2>
          </div>

          <div className="max-w-2xl mx-auto space-y-4 text-center mb-10">
            <p className="font-body text-sm text-[#d4c4a8]/70 leading-relaxed">
              The Marketing Alchemist sees patterns where others see chaos. We translate intuition into strategy, turning creative energy into clarity.
            </p>
            <p className="font-body text-sm text-[#d4c4a8]/70 leading-relaxed">
              Built for founders, creators, and artistic teams who want clarity — not noise. A consulting firm with a twist: we lead with intuition and strategy.
            </p>
            <p className="font-body text-sm text-[#d4c4a8]/70 leading-relaxed italic">
              Where brand psychology, creative intuition, and practical marketing wisdom meet — one card at a time.
            </p>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => navigate('/about')}
              className="inline-flex items-center gap-2 text-sm font-display tracking-widest text-[#e89b47] hover:text-[#e8dcc8] transition-colors uppercase"
              data-testid="our-story-read-more-btn"
            >
              Read the Full Story
              <span className="inline-block">→</span>
            </button>
          </div>
        </section>

        {/* From the TMT Blog */}
        {latestBlogPosts.length > 0 && (
          <section className="mt-24 max-w-5xl mx-auto px-2" data-testid="tmt-blog-section">
            <div className="text-center mb-10">
              <p className="font-display text-xs tracking-[0.3em] text-[#e89b47] mb-4 uppercase">
                TMT Blog
              </p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#e8dcc8] leading-tight mb-4">
                Latest <span className="text-[#e89b47]">Reflections</span>
              </h2>
              <p className="font-body text-sm text-[#d4c4a8]/60 max-w-xl mx-auto">
                Insights on brand, storytelling, and the quiet moments where intuition meets strategy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {latestBlogPosts.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => navigate(`/blog/${p.slug}`)}
                  className="group text-left rounded-2xl overflow-hidden bg-[#1e2a4a]/40 border border-[#d4c4a8]/10 hover:border-[#e89b47]/40 transition-all hover:shadow-lg hover:shadow-[#e89b47]/5"
                  data-testid={`tmt-blog-card-${p.slug}`}
                >
                  <div className="aspect-[16/9] bg-[#0f1f35] overflow-hidden">
                    <CoverImage
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-body text-xs text-[#e89b47] tracking-wider uppercase mb-2">
                      {p.published_at ? new Date(p.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                    </p>
                    <h3 className="font-display text-lg text-[#e8dcc8] leading-snug mb-2 group-hover:text-[#e89b47] transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="font-body text-sm text-[#d4c4a8]/70 leading-relaxed line-clamp-2">
                        {p.excerpt}
                      </p>
                    )}
                    <span className="mt-3 inline-flex items-center gap-1.5 font-display text-xs text-[#e89b47] tracking-wider uppercase">
                      Read more →
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => navigate('/blog')}
                className="inline-flex items-center gap-2 text-sm font-display tracking-widest text-[#e89b47] hover:text-[#e8dcc8] transition-colors uppercase"
                data-testid="tmt-blog-view-all-btn"
              >
                View All Posts
                <span className="inline-block">→</span>
              </button>
            </div>
          </section>
        )}

        {/* Reflections (Testimonials) */}
        <Reflections />

        {/* Footer */}
        <SiteFooter />
      </div>
      
      {/* YouTube Player - floating mini player */}
      {youtubeUrl && (
        <YouTubePlayer 
          videoUrl={youtubeUrl}
          onClose={handleClosePlayer}
          isMuted={isMuted}
        />
      )}
      
      {/* Reading Form Modal */}
      <ReadingForm
        isOpen={showReadingForm}
        onClose={() => setShowReadingForm(false)}
        onSubmit={handleReadingSubmit}
      />

      {/* Quick Reading Form */}
      <QuickReadingForm
        isOpen={showQuickForm}
        onClose={() => setShowQuickForm(false)}
        onSubmit={handleQuickFormSubmit}
      />
      
      {/* Reading Result Screen */}
      <ReadingResult
        isOpen={showReadingResult}
        readingData={readingData}
        onClose={handleReadingClose}
        userName={null}
      />
      
      {/* Shared Reading View */}
      {sharedReadingId && (
        <SharedReading
          readingId={sharedReadingId}
          onBack={() => {
            setSharedReadingId(null);
            // Clear URL param
            window.history.replaceState({}, '', window.location.pathname);
          }}
        />
      )}
    </div>
  );
};

const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => { initGA(); }, []);
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <Routes>
        <Route path="/consulting" element={<ConsultingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/admin/blog" element={<BlogAdminPage />} />
        <Route path="/admin/analytics" element={<AnalyticsAdminPage />} />
        <Route path="/*" element={<TarotApp />} />
      </Routes>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#1e2a4a',
            border: '1px solid rgba(212, 196, 168, 0.3)',
            color: '#e8dcc8',
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
